import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  Design, DesignElement, DesignRow, DesignColumn,
  BuilderMode, EditorConfig, PanelTab, ElementStyles,
} from '../types/editor.types';
import { generateId, deepClone } from '../utils';

const defaultDesign = (mode: BuilderMode): Design => ({
  id: generateId(),
  version: '1.0',
  mode,
  body: { rows: [], styles: { backgroundColor: '#ffffff', padding: '0px' } },
  metadata: {},
});

interface EditorState {
  design: Design;
  config: EditorConfig;
  selectedElementId: string | null;
  selectedRowId: string | null;
  hoveredElementId: string | null;
  activePanel: PanelTab;
  previewMode: 'desktop' | 'mobile' | 'tablet';
  isDirty: boolean;
  isSaving: boolean;

  initEditor: (config: EditorConfig, initialDesign?: Design) => void;
  loadDesign: (design: Design) => void;
  clearDesign: () => void;

  addRow: (afterRowId?: string) => void;
  addRowWithLayout: (columns: number, afterRowId?: string) => void;
  insertBlockRow: (row: DesignRow, afterRowId?: string) => void;
  removeRow: (rowId: string) => void;
  moveRow: (rowId: string, direction: 'up' | 'down') => void;
  updateRowStyles: (rowId: string, styles: Partial<ElementStyles>) => void;
  duplicateRow: (rowId: string) => void;

  addElement: (rowId: string, columnId: string, element: DesignElement, index?: number) => void;
  removeElement: (rowId: string, columnId: string, elementId: string) => void;
  updateElement: (elementId: string, updates: Partial<DesignElement>) => void;
  updateElementStyles: (elementId: string, styles: Partial<ElementStyles>) => void;
  moveElement: (fr: string, fc: string, fi: number, tr: string, tc: string, ti: number) => void;
  duplicateElement: (rowId: string, columnId: string, elementId: string) => void;

  selectElement: (id: string | null, rowId?: string) => void;
  setHoveredElement: (id: string | null) => void;
  setActivePanel: (panel: PanelTab) => void;
  setPreviewMode: (mode: 'desktop' | 'mobile' | 'tablet') => void;
  setIsSaving: (v: boolean) => void;
  markDirty: () => void;
  markClean: () => void;
}

export const useEditorStore = create<EditorState>()(
  immer((set) => ({
    design: defaultDesign('email'),
    config: { mode: 'email' },
    selectedElementId: null,
    selectedRowId: null,
    hoveredElementId: null,
    activePanel: 'tools',
    previewMode: 'desktop',
    isDirty: false,
    isSaving: false,

    initEditor: (config, initialDesign) =>
      set((s) => {
        s.config = config;
        s.design = initialDesign ?? defaultDesign(config.mode);
        s.selectedElementId = null;
        s.isDirty = false;
      }),

    loadDesign: (design) =>
      set((s) => { s.design = design; s.isDirty = false; }),

    clearDesign: () =>
      set((s) => { s.design = defaultDesign(s.config.mode); s.isDirty = false; }),

    addRow: (afterRowId) =>
      set((s) => {
        const newRow: DesignRow = {
          id: generateId(),
          columns: [{ id: generateId(), width: 100, elements: [], styles: {} }],
          styles: { padding: '16px', backgroundColor: 'transparent' },
        };
        if (!afterRowId) {
          s.design.body.rows.push(newRow);
        } else {
          const idx = s.design.body.rows.findIndex((r: DesignRow) => r.id === afterRowId);
          s.design.body.rows.splice(idx + 1, 0, newRow);
        }
        s.isDirty = true;
      }),

    addRowWithLayout: (columns, afterRowId) =>
      set((s) => {
        const colWidth = Math.floor(100 / columns);
        const newRow: DesignRow = {
          id: generateId(),
          columns: Array.from({ length: columns }, (_, i) => ({
            id: generateId(),
            width: i === columns - 1 ? 100 - colWidth * (columns - 1) : colWidth,
            elements: [],
            styles: {},
          })),
          styles: { padding: '16px', backgroundColor: 'transparent' },
        };
        if (!afterRowId) {
          s.design.body.rows.push(newRow);
        } else {
          const idx = s.design.body.rows.findIndex((r: DesignRow) => r.id === afterRowId);
          s.design.body.rows.splice(idx + 1, 0, newRow);
        }
        s.isDirty = true;
      }),

    insertBlockRow: (row, afterRowId) =>
      set((s) => {
        if (!afterRowId) {
          s.design.body.rows.push(row);
        } else {
          const idx = s.design.body.rows.findIndex((r: DesignRow) => r.id === afterRowId);
          s.design.body.rows.splice(idx + 1, 0, row);
        }
        s.isDirty = true;
      }),

    removeRow: (rowId) =>
      set((s) => {
        s.design.body.rows = s.design.body.rows.filter((r: DesignRow) => r.id !== rowId);
        if (s.selectedRowId === rowId) s.selectedRowId = null;
        s.isDirty = true;
      }),

    moveRow: (rowId, direction) =>
      set((s) => {
        const idx = s.design.body.rows.findIndex((r: DesignRow) => r.id === rowId);
        const target = direction === 'up' ? idx - 1 : idx + 1;
        if (target < 0 || target >= s.design.body.rows.length) return;
        const [row] = s.design.body.rows.splice(idx, 1);
        s.design.body.rows.splice(target, 0, row);
        s.isDirty = true;
      }),

    updateRowStyles: (rowId, styles) =>
      set((s) => {
        const row = s.design.body.rows.find((r: DesignRow) => r.id === rowId);
        if (row) { row.styles = { ...row.styles, ...styles }; s.isDirty = true; }
      }),

    duplicateRow: (rowId) =>
      set((s) => {
        const idx = s.design.body.rows.findIndex((r: DesignRow) => r.id === rowId);
        if (idx === -1) return;
        const clone: DesignRow = deepClone(s.design.body.rows[idx] as DesignRow);
        clone.id = generateId();
        clone.columns = clone.columns.map((c: DesignColumn) => ({
          ...c, id: generateId(),
          elements: c.elements.map((e: DesignElement) => ({ ...e, id: generateId() })),
        }));
        s.design.body.rows.splice(idx + 1, 0, clone);
        s.isDirty = true;
      }),

    addElement: (rowId, columnId, element, index) =>
      set((s) => {
        const row = s.design.body.rows.find((r: DesignRow) => r.id === rowId);
        const col = row?.columns.find((c: DesignColumn) => c.id === columnId);
        if (!col) return;
        const el = { ...element, id: generateId() };
        if (index !== undefined) col.elements.splice(index, 0, el);
        else col.elements.push(el);
        s.selectedElementId = el.id;
        s.isDirty = true;
      }),

    removeElement: (rowId, columnId, elementId) =>
      set((s) => {
        const row = s.design.body.rows.find((r: DesignRow) => r.id === rowId);
        const col = row?.columns.find((c: DesignColumn) => c.id === columnId);
        if (!col) return;
        col.elements = col.elements.filter((e: DesignElement) => e.id !== elementId);
        if (s.selectedElementId === elementId) s.selectedElementId = null;
        s.isDirty = true;
      }),

    updateElement: (elementId, updates) =>
      set((s) => {
        for (const row of s.design.body.rows as DesignRow[]) {
          for (const col of row.columns) {
            const idx = col.elements.findIndex((e: DesignElement) => e.id === elementId);
            if (idx !== -1) {
              col.elements[idx] = { ...col.elements[idx], ...updates } as DesignElement;
              s.isDirty = true;
              return;
            }
          }
        }
      }),

    updateElementStyles: (elementId, styles) =>
      set((s) => {
        for (const row of s.design.body.rows as DesignRow[]) {
          for (const col of row.columns) {
            const idx = col.elements.findIndex((e: DesignElement) => e.id === elementId);
            if (idx !== -1) {
              col.elements[idx].styles = { ...col.elements[idx].styles, ...styles };
              s.isDirty = true;
              return;
            }
          }
        }
      }),

    moveElement: (fr, fc, fi, tr, tc, ti) =>
      set((s) => {
        const fromRow = (s.design.body.rows as DesignRow[]).find((r) => r.id === fr);
        const fromCol = fromRow?.columns.find((c) => c.id === fc);
        const toRow = (s.design.body.rows as DesignRow[]).find((r) => r.id === tr);
        const toCol = toRow?.columns.find((c) => c.id === tc);
        if (!fromCol || !toCol) return;
        const [el] = fromCol.elements.splice(fi, 1);
        toCol.elements.splice(ti, 0, el);
        s.isDirty = true;
      }),

    duplicateElement: (rowId, columnId, elementId) =>
      set((s) => {
        const row = (s.design.body.rows as DesignRow[]).find((r) => r.id === rowId);
        const col = row?.columns.find((c) => c.id === columnId);
        if (!col) return;
        const idx = col.elements.findIndex((e: DesignElement) => e.id === elementId);
        if (idx === -1) return;
        const clone: DesignElement = deepClone(col.elements[idx] as DesignElement);
        clone.id = generateId();
        col.elements.splice(idx + 1, 0, clone);
        s.selectedElementId = clone.id;
        s.isDirty = true;
      }),

    selectElement: (id, rowId) =>
      set((s) => { s.selectedElementId = id; s.selectedRowId = rowId ?? null; }),

    setHoveredElement: (id) =>
      set((s) => { s.hoveredElementId = id; }),

    setActivePanel: (panel) =>
      set((s) => { s.activePanel = panel; }),

    setPreviewMode: (mode) =>
      set((s) => { s.previewMode = mode; }),

    setIsSaving: (v) =>
      set((s) => { s.isSaving = v; }),

    markDirty: () =>
      set((s) => { s.isDirty = true; }),

    markClean: () =>
      set((s) => { s.isDirty = false; }),
  }))
);
